import { LinkButton } from '@pmi/dsm-react';
import { ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import { toDayMonthYear } from '../../../../../../shared/helpers';
import { UserCreatedViaType } from '../../../../../../shared/models/UserModels';
import { accountSelector } from '../../../../../../shared/store/shared/accountSlice';
import { addUsersResend, usersResendList } from '../../../resendActionSlice';
import { useResendUserInvitationMutation } from '../apiSlice';
import { TableCellProps } from '../models';
import styles from './accessmethodcell.module.scss';

function AccessCell({ row }: TableCellProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const dispatch = useDispatch();
  const {
    id,
    email,
    invitationStatus = 'Pending',
    dateRegistered: created = null,
    removedDate: removed = null,
    hasFailedEmail = false,
    userCreatedVia,
  } = row.original;
  const isInResendList = useSelector(usersResendList).includes(email);
  const disabled = row.original?.isRemoved;
  const [resendInvitation, resendInvitationRequest] =
    useResendUserInvitationMutation();
  if (disabled) {
    return (
      <>
        <div>User Disabled</div>
        {invitationStatus !== 'Pending' && removed && (
          <span>{toDayMonthYear(removed)}</span>
        )}
      </>
    );
  }

  const getDomainFromEmail = (): string => {
    const lastAtIndex = email?.lastIndexOf('@');
    return lastAtIndex > -1 ? email.substring(lastAtIndex + 1) : '';
  };

  const renderInvitationType = () => (
    <span>
      {userCreatedVia === UserCreatedViaType.EmailInvite
        ? 'Email Invite'
        : `Domain (${getDomainFromEmail()})`}
    </span>
  );

  const onResendClick = (
    accountId: string,
    emailAdd: string,
    accountUserId: string = '',
  ) => {
    resendInvitation({
      accountUserId,
    })
      .unwrap()
      .then(() => {
        const message = (): ReactNode => (
          <>
            Invite resent to <span data-exclude-lg="true">{emailAdd}</span>
          </>
        );
        toastWithAnalytics(message(), 'success');
        resendInvitationRequest.reset();
      })
      .catch(() => {
        toastWithAnalytics(
          'Could not resend invitation. Please try again.',
          'danger',
        );
      });
  };

  const renderRegistered = () => (
    <span className="d-block">
      {invitationStatus !== 'Pending' ? (
        <span>{toDayMonthYear(created)}</span>
      ) : (
        <span className={styles.pendingInvite}>
          <span className="d-block me-1">
            {hasFailedEmail ? 'Undeliverable' : 'Pending'}
          </span>
          {resendInvitationRequest.isLoading ? (
            <Spinner size="sm" className="text-primary ms-1" />
          ) : (
            <LinkButton
              size="xs"
              className={styles.resendInvite}
              titleText="Resend"
              disabled={isInResendList}
              onClick={
                isInResendList
                  ? () => null
                  : () => {
                      dispatch(addUsersResend(email));
                      onResendClick(currentAccountId, email, id);
                    }
              }
            />
          )}
        </span>
      )}
    </span>
  );

  return (
    <div className={`${styles.cell}`}>
      {invitationStatus !== 'Pending' && renderInvitationType()}
      {email && renderRegistered()}
    </div>
  );
}

export default AccessCell;
